import UnsavedWarningModal, { IUnsavedWarningModal } from '@components/atoms/UnsavedWarningModal';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { NavigateOptions, To, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { BooleanUtil, useBoolean } from 'src/hooks/use-boolean';

const emptyDeepLink: IDeeplinkContext = {
  organizationIdParam: undefined,
  locationIdParam: undefined,
  targetTabParam: undefined,
  clearOrganizationIdParam: () => {},
  clearLocationIdParam: () => {},
  clearTargetTabParam: () => {},
  shouldBlockNavigation: undefined,
  safeNavigateToSectionInTab: () => {},
  navigateToSectionInTab: () => {},
  overrideModal: () => {},
};

type IExposedUnsavedWarningModal = Omit<IUnsavedWarningModal, 'navigateCta'>;

export interface IDeeplinkContext {
  organizationIdParam: undefined | number;
  locationIdParam: undefined | number;
  targetTabParam: undefined | string;
  clearOrganizationIdParam: VoidFunction;
  clearLocationIdParam: VoidFunction;
  clearTargetTabParam: VoidFunction;
  shouldBlockNavigation: undefined | BooleanUtil;
  safeNavigateToSectionInTab: (path: To, targetTab?: string, options?: NavigateOptions) => void;
  navigateToSectionInTab: (path: To, targetTab?: string, options?: NavigateOptions) => void;
  overrideModal: (costomization: IExposedUnsavedWarningModal) => void;
}

const DeepLinkContext = createContext<IDeeplinkContext>(emptyDeepLink);

const DeepLinkContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [organizationId, setOrganizationId] = useState<number>();
  const [locationId, setLocationId] = useState<number>();
  const [targetTab, setTargetTab] = useState<string>();
  const shouldBlockNavigation = useBoolean(false);
  const { state } = useLocation();

  const showConfirmDialog = useBoolean(false);
  const [modalOverride, setModalOverride] = useState<IUnsavedWarningModal>();

  const navigate = useNavigate();

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlockNavigation.value) e.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlockNavigation.value]);

  useEffect(() => {
    const _locationId = searchParams.get('locationId');
    const _organizationId = searchParams.get('organizationId');
    const _targetTab = searchParams.get('targetTab');

    setLocationId(_locationId ? +_locationId : undefined);
    setOrganizationId(_organizationId ? +_organizationId : undefined);
    setTargetTab(_targetTab ? _targetTab : undefined);
  }, [searchParams]);

  const clearOrganizationIdParam = () => {
    if (!searchParams.has('organizationId')) return;
    searchParams.delete('organizationId');
    setSearchParams(searchParams, { replace: true, state });
  };

  const clearLocationIdParam = () => {
    if (!searchParams.has('locationId')) return;
    searchParams.delete('locationId');
    setSearchParams(searchParams, { replace: true, state });
  };

  const clearTargetTabParam = () => {
    if (!searchParams.has('targetTab')) return;
    searchParams.delete('targetTab');
    setSearchParams(searchParams, { replace: true, state });
  };

  const safeNavigateToSectionInTab = (path: To, tab?: string, options?: NavigateOptions) => {
    const searchParams = new URLSearchParams(window.location.search);
    if (tab) searchParams.set('targetTab', tab);
    navigate(`${path}?${searchParams.toString()}`, options);
    shouldBlockNavigation.onFalse();
    setModalOverride(undefined);
  };

  const navigateToSectionInTab = (path: To, tab?: string, options?: NavigateOptions) => {
    const safeNavigate = () => safeNavigateToSectionInTab(path, tab, options);
    if (!shouldBlockNavigation.value) safeNavigate();
    else {
      setModalOverride({ ...modalOverride, navigateCta: safeNavigate });
      showConfirmDialog.onTrue();
    }
  };

  const overrideModal = (override: IExposedUnsavedWarningModal) => setModalOverride(override);

  return (
    <DeepLinkContext.Provider
      value={{
        organizationIdParam: organizationId,
        locationIdParam: locationId,
        targetTabParam: targetTab,
        clearOrganizationIdParam,
        clearLocationIdParam,
        clearTargetTabParam,
        shouldBlockNavigation,
        safeNavigateToSectionInTab,
        navigateToSectionInTab,
        overrideModal,
      }}
    >
      {children}
      {modalOverride && <UnsavedWarningModal handeler={showConfirmDialog} modalData={modalOverride} />}
    </DeepLinkContext.Provider>
  );
};

const useDeeplinkContext = () => {
  const context = useContext(DeepLinkContext);
  if (context === undefined) {
    throw new Error('useDeeplinkContext must be used within a DeepLinkContextProvider');
  }
  return context;
};

export { useDeeplinkContext, DeepLinkContextProvider };
